import React, { useState } from 'react';
import { ArrowRight, ExternalLink, Layers, Eye } from 'lucide-react';
import { portfolioData } from '../../data/portfolioData';
import { ProjectItem } from '../../types';

interface PortfolioSectionProps {
  onSelectProject: (project: ProjectItem) => void;
  onViewAllProjects: () => void;
}

export const PortfolioSection: React.FC<PortfolioSectionProps> = ({ onSelectProject, onViewAllProjects }) => {
  const [activeTab, setActiveTab] = useState<string>('All');
  const categories = ['All', 'Web Platforms', 'Mobile Experiences', 'Business Software', 'AI & Automation', 'E-Commerce', 'Digital Marketing'];

  const filteredProjects = activeTab === 'All' 
    ? portfolioData 
    : portfolioData.filter(p => p.category === activeTab);

  return (
    <section id="portfolio-section" className="bg-[#050A13] py-20 border-b border-[#1b2f4a] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-10 space-y-3">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-8 bg-[#FF851B]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B]">
              OUR PORTFOLIO
            </span>
            <span className="h-px w-8 bg-[#FF851B]" />
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight font-display">
            Ideas Transformed Into Digital Experiences
          </h2>

          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            Explore representative technology architecture and cross-platform solutions engineered by our teams.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                activeTab === cat
                  ? 'bg-[#159DE8] text-white shadow-glow-blue'
                  : 'bg-[#0c1a2d] border border-[#1b2f4a] text-[#8FA0B8] hover:text-white hover:bg-[#10223b]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Portfolio Cards Grid - Limited to First 3 for a Streamlined Homepage */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.slice(0, 3).map((project) => (
            <div
              key={project.id}
              className="group rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] overflow-hidden hover:border-[#159DE8]/50 transition-all duration-300 shadow-card-dark flex flex-col justify-between"
            >
              <div>
                {/* Image / Mockup preview */}
                <div className="relative aspect-[16/10] overflow-hidden bg-[#050A13] border-b border-[#1b2f4a]">
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c1a2d] via-[#0c1a2d]/20 to-transparent opacity-70" />
                  
                  {/* Category Pill */}
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-md bg-[#050A13]/90 backdrop-blur-md border border-[#1b2f4a] text-[11px] font-semibold text-[#159DE8]">
                      {project.category}
                    </span>
                  </div>

                  {/* Quick Action Button */}
                  <button
                    onClick={() => onSelectProject(project)}
                    className="absolute inset-0 flex items-center justify-center bg-[#050A13]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    aria-label={`View details for ${project.title}`}
                  >
                    <span className="px-4 py-2 rounded-full bg-white text-[#050A13] text-xs font-bold shadow-lg flex items-center gap-1.5 hover:scale-105 transition-transform">
                      <Eye size={14} />
                      <span>View Case Details</span>
                    </span>
                  </button>
                </div>

                {/* Content */}
                <div className="p-5 space-y-2">
                  <h3 className="text-base font-bold text-white group-hover:text-[#FF851B] transition-colors font-display">
                    {project.title}
                  </h3>
                  <p className="text-xs text-[#8FA0B8] leading-relaxed line-clamp-2">
                    {project.summary}
                  </p>
                </div>
              </div>

              {/* Tags & Action */}
              <div className="px-5 pb-5 pt-3 border-t border-[#1b2f4a]/50 flex items-center justify-between">
                <div className="flex flex-wrap gap-1.5">
                  {project.tags.slice(0, 2).map((tag, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-[#050A13] border border-[#1b2f4a] text-[#8FA0B8]">
                      {tag}
                    </span>
                  ))}
                </div>

                <button
                  onClick={() => onSelectProject(project)}
                  className="text-xs font-semibold text-[#159DE8] group-hover:text-[#FF851B] flex items-center gap-1 transition-colors"
                >
                  <span>Details</span>
                  <ArrowRight size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* View All Projects Footer Link */}
        <div className="mt-10 text-center">
          <button
            onClick={onViewAllProjects}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-[#1b2f4a] bg-[#071426] text-xs font-semibold text-white hover:border-[#159DE8] hover:text-[#159DE8] transition-all"
          >
            <span>Explore All Enterprise Case Studies</span>
            <ArrowRight size={14} />
          </button>
        </div>

      </div>
    </section>
  );
};
